import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

const VISITOR_KEY = 'np-welcome-visitor-v1';
const SESSION_KEY = 'np-welcome-session-v1';
const SHOWN_KEY = 'np-welcome-shown-v1';
const WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Private, contact-free welcome guidance. Native modal semantics contain focus;
 * the theme owns scroll locking. Storage failure disables automatic display.
 * @typedef {{dialog: HTMLDialogElement}} Refs
 * @extends Component<Refs>
 */
class NuzzleWelcome extends Component {
  requiredRefs = ['dialog'];
  #events;
  #timer;
  #interactionTimer;
  #engaged = false;
  #ready = false;
  #attempted = false;
  #previousFocus;
  #boundDialog;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#initialize();
  }

  #initialize() {
    this.#engaged = false;
    this.#ready = false;
    this.#attempted = false;
    this.#events = new AbortController();
    const { signal } = this.#events;
    const { dialog } = this.refs;
    this.#boundDialog = dialog;
    if (typeof dialog.showModal !== 'function') return;

    dialog.addEventListener('close', this.#afterClose, { signal });
    dialog.addEventListener('click', this.#backdropClick, { signal });
    // Escape uses the browser's native cancel/close path, including focus return.
    dialog.addEventListener('keydown', this.#containFocus, { signal });

    if (this.dataset.editor === 'true') {
      if (this.dataset.editorPreview === 'true') this.#open();
      return;
    }
    if (!this.#isFirstSession()) return;

    const delay = Math.min(30, Math.max(12, Number(this.dataset.delay) || 15)) * 1000;
    this.#timer = window.setTimeout(() => {
      this.#ready = true;
      this.#tryAutomaticOpen();
    }, delay);
    for (const type of ['scroll', 'pointerup', 'keyup']) {
      window.addEventListener(type, this.#engage, { passive: true, signal });
    }
    window.addEventListener('pagehide', this.#stopAutomatic, { once: true, signal });
  }

  disconnectedCallback() {
    this.#teardown();
    super.disconnectedCallback();
  }

  #teardown() {
    this.#stopAutomatic();
    this.#events?.abort();
    const dialog = this.#boundDialog;
    if (dialog?.open) {
      dialog.close();
      this.#afterClose();
    }
    if (dialog) unlockScroll(dialog);
  }

  /** Close button and gentle dismissal share the native close/focus path. */
  closeDialog() {
    this.refs.dialog.close();
  }

  #isFirstSession() {
    try {
      let session = sessionStorage.getItem(SESSION_KEY);
      if (!session) {
        session = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(SESSION_KEY, session);
      }
      const visitor = localStorage.getItem(VISITOR_KEY);
      if (visitor && visitor !== session) return false;
      localStorage.setItem(VISITOR_KEY, session);
      return !this.#shownRecently();
    } catch {
      return false;
    }
  }

  #shownRecently() {
    const shown = Number(localStorage.getItem(SHOWN_KEY) || 0);
    return !Number.isFinite(shown) || shown > Date.now() - WEEK;
  }

  #engage = (event) => {
    if (!event.isTrusted || this.#engaged) return;
    this.#engaged = true;
    // Let the visitor's click/key finish instead of replacing its focused target.
    this.#interactionTimer = window.setTimeout(() => this.#tryAutomaticOpen(), 350);
  };

  #tryAutomaticOpen() {
    if (!this.#ready || !this.#engaged || this.#attempted) return;
    // A background tab must not consume the only attempt. Wait for a fresh,
    // trusted interaction after it becomes visible instead of opening on focus.
    if (document.visibilityState !== 'visible') {
      this.#engaged = false;
      return;
    }
    this.#attempted = true;
    this.#stopAutomatic();
    const active = document.activeElement;
    if (active?.matches('input, textarea, select, [contenteditable="true"]')) return;
    if (this.#anotherPanelIsOpen()) return;
    try {
      if (this.#shownRecently()) return;
      // Stamp before display so navigation or another tab cannot immediately repeat it.
      localStorage.setItem(SHOWN_KEY, String(Date.now()));
    } catch {
      return;
    }
    this.#open();
  }

  #anotherPanelIsOpen() {
    const panels = document.querySelectorAll('dialog[open], [aria-modal="true"], #header-group details[open], #loyaltylion-popup-frame, iframe[name="loyaltylion-popup-frame"]');
    return [...panels].some((panel) => {
      if (panel === this.refs.dialog) return false;
      const rect = panel.getBoundingClientRect();
      const style = getComputedStyle(panel);
      return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
    });
  }

  #open() {
    const { dialog } = this.refs;
    if (dialog.open || !this.isConnected) return;
    this.#previousFocus = document.activeElement;
    try {
      dialog.showModal();
      lockScroll(dialog);
    } catch {
      unlockScroll(dialog);
    }
  }

  #afterClose = () => {
    unlockScroll(this.refs.dialog);
    const previous = this.#previousFocus;
    if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
  };

  #backdropClick = (event) => {
    if (event.target !== this.refs.dialog) return;
    const rect = this.refs.dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog();
  };

  // Native modal containment is supplemented for consistent Shift+Tab wrapping.
  #containFocus = (event) => {
    if (event.key !== 'Tab') return;
    const controls = [...this.refs.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')].filter((element) => element.getClientRects().length);
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  #stopAutomatic = () => {
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#interactionTimer);
    for (const type of ['scroll', 'pointerup', 'keyup']) window.removeEventListener(type, this.#engage);
  };
}

if (!customElements.get('nuzzle-welcome-component')) customElements.define('nuzzle-welcome-component', NuzzleWelcome);
